import React, { useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { auth } from '../utils/backend'
import { useAuthStore } from '../stores'
import { isStrongPassword } from '../utils/security'

export function ResetPasswordModal() {
  const { clearRecoveryState } = useAuthStore()
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!password) {
      setError('Password is required')
      return
    }
    if (!isStrongPassword(password)) {
      setError('Password must be 8+ chars with uppercase, lowercase, and numbers')
      return
    }

    setLoading(true)
    try {
      await auth.updateUserPassword(password)
      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update password')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-6">
      <div className="w-full max-w-md p-8 bg-gray-900 rounded-lg border border-gray-800 shadow-2xl">
        <h1 className="text-2xl font-bold text-center mb-2 text-green-500">
          Set New Password
        </h1>
        <p className="text-center text-gray-400 mb-6 text-sm">
          Please enter a new password for your account.
        </p>

        {success ? (
          <div className="text-center">
            <div className="p-4 mb-6 bg-green-900/30 border border-green-700 text-green-200 rounded text-sm">
              ✓ Password updated successfully!
            </div>
            <button
              onClick={() => clearRecoveryState()}
              className="w-full py-2 bg-green-600 hover:bg-green-700 rounded font-semibold text-white transition"
            >
              Go to Dashboard
            </button>
          </div>
        ) : (
          <form onSubmit={handleReset} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-900/30 border border-red-700 text-red-200 rounded text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded text-white placeholder-gray-500 focus:border-green-500 focus:outline-none pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Min 8 chars, uppercase, lowercase, number
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded font-semibold text-white transition mt-4"
            >
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
